/**
 * Iconografia do Viajandos — os 30 ícones da Fundação Visual §5.
 *
 * Grade 24, traço 2px, pontas e junções arredondadas, `currentColor`, sem
 * preenchimento. O traço de 2 é escolha de marca: o ícone do app é pesado e
 * chapado, e um contorno fino ficaria estranho ao lado dele.
 *
 * Substituem os glifos tipográficos (‹ › ✓ ⇅ ✦ 🗓) e os quadrados coloridos
 * das abas. Todo ícone é decorativo por padrão (`aria-hidden`): o texto ao
 * lado, ou o `aria-label` do botão, é quem carrega o significado. Um ícone
 * sozinho num botão sem rótulo é um botão sem nome — não faça isso.
 */
import type { SVGProps } from 'react';

const PATHS = {
  // Navegação — as 5 abas
  home: (
    <>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5.5 9.5V20h13V9.5" />
      <path d="M9.5 20v-5.5h5V20" />
    </>
  ),
  roteiro: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="3" />
      <path d="M3 10h18M8 3v4M16 3v4" />
      <path d="M7.5 14.5h4M7.5 17.5h9" />
    </>
  ),
  mapa: (
    <>
      <path d="M9 3 3 5.5v15L9 18l6 3 6-2.5v-15L15 6 9 3Z" />
      <path d="M9 3v15M15 6v15" />
    </>
  ),
  memorias: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="3" />
      <circle cx="8.5" cy="9.5" r="1.8" />
      <path d="m3.5 16.5 4.2-3.6 3.3 2.8 3.4-3.6 6.1 5.4" />
    </>
  ),
  mais: <path d="M4 7h16M4 12h16M4 17h10" />,

  // Ação e sistema
  criar: <path d="M12 5v14M5 12h14" />,
  concluir: <path d="m4.5 12.5 5 5 10-11" />,
  voltar: <path d="M15 5 8 12l7 7" />,
  avancar: <path d="m9 5 7 7-7 7" />,
  fechar: <path d="M6 6l12 12M18 6 6 18" />,
  reordenar: <path d="M8 9 5 6 2.5 9M5 6v12M16 15l3 3 2.5-3M19 18V6" />,
  maisOpcoes: (
    <>
      <circle cx="5.5" cy="12" r="1.4" fill="currentColor" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" />
      <circle cx="18.5" cy="12" r="1.4" fill="currentColor" />
    </>
  ),
  compartilhar: (
    <>
      <path d="M12 3v12M8 6.5 12 3l4 3.5" />
      <path d="M5 14v5.5h14V14" />
    </>
  ),
  copiar: (
    <>
      <rect x="8" y="8" width="12" height="12" rx="3" />
      <path d="M16 5.5A2.5 2.5 0 0 0 13.5 4H7a3 3 0 0 0-3 3v6.5A2.5 2.5 0 0 0 5.5 16" />
    </>
  ),
  buscar: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m16 16 4.5 4.5" />
    </>
  ),
  notificacoes: (
    <>
      <path d="M6 10a6 6 0 0 1 12 0c0 4 1.5 5.5 1.5 5.5h-15S6 14 6 10Z" />
      <path d="M10 19a2.2 2.2 0 0 0 4 0" />
    </>
  ),
  // Engrenagem do Lucide (`settings`, ISC): o desenho anterior (círculo com
  // raios) lia como sol, não como configuração (teste físico, 13/09).
  configuracoes: (
    <>
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  destaque: (
    <path d="M12 3.5 13.9 8l4.6.4-3.5 3 1.1 4.5L12 13.6 7.9 15.9 9 11.4l-3.5-3L10.1 8 12 3.5Z" />
  ),
  local: (
    <>
      <path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.5" />
    </>
  ),
  horario: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v5.2l3.4 2" />
    </>
  ),
  pessoas: (
    <>
      <circle cx="9" cy="8.5" r="3.2" />
      <path d="M3.5 19c.6-3 2.8-4.6 5.5-4.6s4.9 1.6 5.5 4.6" />
      <path d="M16.5 6.2a3.2 3.2 0 0 1 0 6.1M17.5 14.8c2.1.5 3.4 2 3.9 4.2" />
    </>
  ),
  dinheiro: (
    <>
      <rect x="3" y="6.5" width="18" height="12" rx="3" />
      <circle cx="12" cy="12.5" r="2.6" />
      <path d="M6.5 12.5h.01M17.5 12.5h.01" />
    </>
  ),
  documento: (
    <>
      <path d="M6 3.5h7.5L19 9v11.5H6V3.5Z" />
      <path d="M13.5 3.5V9H19" />
    </>
  ),
  semPermissao: (
    <>
      <rect x="4" y="7.5" width="16" height="12.5" rx="3" />
      <path d="M8.5 7.5V6a3.5 3.5 0 0 1 7 0v1.5" />
    </>
  ),
  aviso: (
    <>
      <path d="M12 4.5 20.5 19H3.5L12 4.5Z" />
      <path d="M12 10v4M12 16.6h.01" />
    </>
  ),
  sincronizar: (
    <>
      <path d="M20 12a8 8 0 1 1-2.6-5.9" />
      <path d="M20 4v4.5h-4.5" />
    </>
  ),
  offline: (
    <>
      <path d="M7.5 17.5h9a4 4 0 0 0 .6-8A6 6 0 0 0 6.2 8.6" />
      <path d="M3 3l18 18" />
    </>
  ),
  // Lápis do Lucide (`pencil`, ISC) — o desenho próprio não se lia no
  // celular (teste físico, item 13). Mesma grade 24 e traço 2.
  editar: (
    <>
      <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
      <path d="m15 5 4 4" />
    </>
  ),
  excluir: (
    <>
      <path d="M4.5 6.5h15M9.5 6.5V4h5v2.5" />
      <path d="M6.5 6.5 7.5 20h9l1-13.5" />
    </>
  ),
  sair: (
    <>
      <path d="M13.5 3.5H7a2.5 2.5 0 0 0-2.5 2.5v12A2.5 2.5 0 0 0 7 20.5h6.5" />
      <path d="M17 8.5 20.5 12 17 15.5M20.5 12H10" />
    </>
  ),
  /** Saída externa: abre outro app (mapa, compartilhador). */
  // Rodada 2
  mala: (
    <>
      <rect x="3" y="8" width="18" height="12" rx="2.5" />
      <path d="M8 8V5.5A1.5 1.5 0 0 1 9.5 4h5A1.5 1.5 0 0 1 16 5.5V8" />
      <path d="M8 8v12M16 8v12" />
    </>
  ),
  trofeu: (
    <>
      <path d="M8 4h8v5a4 4 0 0 1-8 0V4Z" />
      <path d="M8 6H5.5A1.5 1.5 0 0 0 4 7.5c0 2 1.5 3.5 4 3.5M16 6h2.5A1.5 1.5 0 0 1 20 7.5c0 2-1.5 3.5-4 3.5" />
      <path d="M12 13v3M9 20h6M10 16h4v4" />
    </>
  ),
  enquete: (
    <>
      <path d="M5 20V10M12 20V4M19 20v-7" />
      <path d="M3 20h18" />
    </>
  ),
  ponto: (
    <>
      <circle cx="12" cy="10" r="3" />
      <path d="M12 21s-6-5.5-6-11a6 6 0 0 1 12 0c0 5.5-6 11-6 11Z" />
      <path d="M4 21h16" />
    </>
  ),
  carro: (
    <>
      <path d="M4 15v-3l2-5h12l2 5v3" />
      <path d="M3 15h18v3H3z" />
      <circle cx="7.5" cy="18" r="1.5" /><circle cx="16.5" cy="18" r="1.5" />
    </>
  ),
  bolo: (
    <>
      <path d="M4 20h16v-6H4zM6 14v-3h12v3M12 11V7M10.5 5.5 12 4l1.5 1.5" />
    </>
  ),
  carimbo: (
    <>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="4" />
      <path d="M12 4v2M12 18v2M4 12h2M18 12h2" />
    </>
  ),
  externo: (
    <>
      <path d="M14 4h6v6" />
      <path d="M20 4 11 13" />
      <path d="M18 13.5V19a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 4 19V8a1.5 1.5 0 0 1 1.5-1.5H11" />
    </>
  ),
  /** Feedback do beta: balão de conversa. */
  feedback: (
    <>
      <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v7a2.5 2.5 0 0 1-2.5 2.5H10l-4.5 4v-4A2.5 2.5 0 0 1 4 13.5v-7Z" />
      <path d="M8 9h8M8 12.5h5" />
    </>
  ),

  // Categorias de despesa (teste físico, item 35) — desenhos do Lucide (ISC),
  // na mesma grade 24 / traço 2: utensils, shopping-cart, fuel, bed,
  // ticket, shopping-bag, gift, chart-column, arrow-down, arrow-up, search.
  alimentacao: (
    <>
      <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
      <path d="M7 2v20" />
      <path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
    </>
  ),
  mercado: (
    <>
      <circle cx="8" cy="21" r="1" /><circle cx="19" cy="21" r="1" />
      <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
    </>
  ),
  combustivel: (
    <>
      <path d="M3 22h12" /><path d="M4 9h10" />
      <path d="M14 22V4a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v18" />
      <path d="M14 13h2a2 2 0 0 1 2 2v2a2 2 0 0 0 2 2a2 2 0 0 0 2-2V9.83a2 2 0 0 0-.59-1.42L18 5" />
    </>
  ),
  hospedagem: (
    <>
      <path d="M2 4v16" /><path d="M2 8h18a2 2 0 0 1 2 2v10" /><path d="M2 17h20" /><path d="M6 8v9" />
    </>
  ),
  ingresso: (
    <>
      <path d="M2 9a3 3 0 0 1 0 6v3a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-3a3 3 0 0 1 0-6V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
      <path d="M13 5v2" /><path d="M13 17v2" /><path d="M13 11v2" />
    </>
  ),
  compras: (
    <>
      <path d="M16 10a4 4 0 0 1-8 0" />
      <path d="M3.103 6.034h17.794" />
      <path d="M3.4 5.467a2 2 0 0 0-.4 1.2V20a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6.667a2 2 0 0 0-.4-1.2l-2-2.667A2 2 0 0 0 17 2H7a2 2 0 0 0-1.6.8z" />
    </>
  ),
  presente: (
    <>
      <rect x="3" y="8" width="18" height="4" rx="1" /><path d="M12 8v13" />
      <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
      <path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5" />
    </>
  ),
  aviao: (
    <>
      <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
    </>
  ),
  grafico: (
    <>
      <path d="M3 3v16a2 2 0 0 0 2 2h16" /><path d="M18 17V9" /><path d="M13 17V5" /><path d="M8 17v-3" />
    </>
  ),
  setaBaixo: (
    <>
      <path d="M12 5v14" /><path d="m19 12-7 7-7-7" />
    </>
  ),
  setaCima: (
    <>
      <path d="m5 12 7-7 7 7" /><path d="M12 19V5" />
    </>
  ),
} as const;

export type IconName = keyof typeof PATHS;

export const ICON_NAMES = Object.keys(PATHS) as IconName[];

export function Icon({
  name,
  size = 24,
  title,
  ...rest
}: {
  name: IconName;
  size?: number;
  /** Só quando o ícone é a única informação. Prefira texto ao lado. */
  title?: string;
} & Omit<SVGProps<SVGSVGElement>, 'name' | 'width' | 'height'>) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      focusable="false"
      data-icone={name}
      {...rest}
    >
      {title && <title>{title}</title>}
      {PATHS[name]}
    </svg>
  );
}
